import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { FilterBar } from '@/components/ui/FilterBar';
import { FormMessage } from '@/components/ui/FormMessage';
import { GalleryCard } from '@/components/ui/GalleryCard';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useInvalidFieldFocus } from '@/hooks/useInvalidFieldFocus';
import { ALL_FILTER, GALLERY_TYPES } from '@/data/categories';
import { createId } from '@/lib/format';
import { isNonEmpty, isValidUrl } from '@/lib/validation';
import type { FormFeedback, GalleryItem, GalleryType } from '@/types';
import styles from './Gallery.module.css';

type TypeFilter = typeof ALL_FILTER | GalleryType;

const FILTER_OPTIONS: readonly TypeFilter[] = [ALL_FILTER, ...GALLERY_TYPES];

interface Errors {
  type?: string;
  title?: string;
  url?: string;
  thumbnail?: string;
}

export interface GalleryProps {
  readonly titleId: string;
  readonly items: readonly GalleryItem[];
  readonly onAdd: (item: GalleryItem) => void;
  readonly onRemove: (id: string) => void;
  /** Put a deleted item back at the position it held. */
  readonly onRestore: (index: number, item: GalleryItem) => void;
}

export function Gallery({ titleId, items, onAdd, onRemove, onRestore }: GalleryProps) {
  const [type, setType] = useState<GalleryType | ''>('');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [feedback, setFeedback] = useState<FormFeedback | null>(null);
  const { formRef, focusFirstInvalid } = useInvalidFieldFocus();
  const [filter, setFilter] = useState<TypeFilter>(ALL_FILTER);
  // Deleting is one click and irreversible, so hold on to the last removal and
  // offer it back rather than asking for confirmation up front.
  const [undoable, setUndoable] = useState<{ index: number; item: GalleryItem } | null>(null);

  const undoRef = useRef<HTMLButtonElement>(null);
  const listHeadingRef = useRef<HTMLHeadingElement>(null);

  const removeItem = (id: string) => {
    const index = items.findIndex((item) => item.id === id);
    const item = items[index];
    if (!item) return;

    pendingFocus.current = 'undo';
    setUndoable({ index, item });
    onRemove(id);
  };

  const undoRemove = () => {
    if (!undoable) return;

    // Restoring into a filter that excludes the item would look like the undo
    // silently failed — and the visitor would add the content again by hand.
    if (filter !== ALL_FILTER && filter !== undoable.item.type) setFilter(ALL_FILTER);

    pendingFocus.current = 'list';
    onRestore(undoable.index, undoable.item);
    setUndoable(null);
  };

  /**
   * Deleting unmounts the card that held focus, and undoing unmounts the bar
   * that held focus — both would otherwise drop the visitor on <body>, at the
   * top of the tab order.
   *
   * The target is recorded by the action itself rather than inferred from how
   * `undoable` changed. Inferring it gets two cases wrong: deleting a second
   * item is a set→set transition and moves focus nowhere, and retiring the
   * offer after an unrelated add looks identical to an undo, yanking focus out
   * of the form the visitor is still using.
   */
  const pendingFocus = useRef<'undo' | 'list' | null>(null);
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;

    pendingFocus.current = null;
    if (target === 'undo') undoRef.current?.focus();
    else listHeadingRef.current?.focus();
  });

  const visible = useMemo(
    () => (filter === ALL_FILTER ? items : items.filter((item) => item.type === filter)),
    [items, filter],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const nextErrors: Errors = {};
    if (!type) nextErrors.type = 'الرجاء اختيار نوع المحتوى.';
    if (!isNonEmpty(title)) nextErrors.title = 'الرجاء إدخال العنوان.';
    if (!isNonEmpty(url)) nextErrors.url = 'الرجاء إدخال الرابط.';
    else if (!isValidUrl(url)) nextErrors.url = 'الرابط غير صحيح — يجب أن يبدأ بـ https://';
    if (isNonEmpty(thumbnail) && !isValidUrl(thumbnail)) {
      nextErrors.thumbnail = 'رابط الصورة غير صحيح — يجب أن يبدأ بـ https://';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !type) {
      setFeedback({ tone: 'error', message: 'الرجاء تصحيح الحقول المميزة ثم إعادة المحاولة.' });
      focusFirstInvalid();
      return;
    }

    onAdd({
      id: createId('gallery'),
      type,
      title: title.trim(),
      description: description.trim(),
      url: url.trim(),
      thumbnail: thumbnail.trim(),
    });

    // A filter for a different type would hide the item that was just added,
    // leaving the success message pointing at nothing. Widen it just enough.
    if (filter !== ALL_FILTER && filter !== type) setFilter(ALL_FILTER);

    // Retire any pending undo: the index it captured describes a list that no
    // longer exists, so restoring would drop the item in the wrong place — and
    // an offer to undo a past deletion has no business lingering over a form
    // the visitor has since used to add something else.
    setUndoable(null);

    setType('');
    setTitle('');
    setUrl('');
    setThumbnail('');
    setDescription('');
    setFeedback({ tone: 'success', message: 'تمت إضافة المحتوى بنجاح!' });
  };

  return (
    <>
      <SectionHeader eyebrow="أمثلة وصور" title="أمثلة وصور وفيديوهات" titleId={titleId} />

      <div className={styles.layout}>
        <form ref={formRef} className={styles.form} onSubmit={submit} noValidate>
          <h3 className={styles.formTitle}>إضافة محتوى جديد</h3>

          <SelectField
            label="نوع المحتوى"
            required
            value={type}
            onChange={(event) => setType(event.target.value as GalleryType | '')}
            {...(errors.type ? { error: errors.type } : {})}
          >
            <option value="">اختر النوع</option>
            {GALLERY_TYPES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectField>

          <TextField
            label="العنوان"
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="أدخل عنوان المحتوى"
            {...(errors.title ? { error: errors.title } : {})}
          />

          <TextField
            label="الرابط (URL)"
            required
            type="url"
            dir="ltr"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://example.com/image.jpg"
            {...(errors.url ? { error: errors.url } : {})}
          />

          <TextField
            label="رابط الصورة المصغرة (اختياري)"
            type="url"
            dir="ltr"
            value={thumbnail}
            onChange={(event) => setThumbnail(event.target.value)}
            placeholder="https://example.com/thumb.jpg"
            {...(errors.thumbnail ? { error: errors.thumbnail } : {})}
          />

          <TextAreaField
            label="الوصف"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="أضف وصفاً قصيراً للمحتوى..."
            rows={4}
          />

          <Button type="submit">إضافة المحتوى</Button>

          <FormMessage feedback={feedback} />
        </form>

        <section aria-labelledby="gallery-list-title">
          <div className={styles.listHead}>
            <h3
              className={styles.formTitle}
              id="gallery-list-title"
              ref={listHeadingRef}
              tabIndex={-1}
            >
              المحتوى المضاف
            </h3>
            <FilterBar
              options={FILTER_OPTIONS}
              value={filter}
              onChange={setFilter}
              label="تصفية حسب النوع"
              compact
            />
          </div>

          {/* The region stays mounted and only its contents change: creating it
              in the same commit as its text leaves some screen readers silent,
              because the region did not exist when the text arrived. */}
          <div role="status" aria-live="polite">
            {undoable ? (
              <div className={styles.undo}>
                <span>تم حذف «{undoable.item.title}».</span>
                <Button ref={undoRef} variant="quiet" onClick={undoRemove}>
                  تراجع
                </Button>
              </div>
            ) : null}
          </div>

          {visible.length > 0 ? (
            <div className={styles.feed}>
              {visible.map((item) => (
                <GalleryCard key={item.id} item={item} onRemove={removeItem} />
              ))}
            </div>
          ) : (
            <EmptyState
              boxed
              body="لا يوجد محتوى بهذا التصنيف بعد — أضف أول عنصر من النموذج المجاور."
            />
          )}
        </section>
      </div>
    </>
  );
}
